/**
 * ProfileSection - the signed-in user's own name and profile image.
 *
 * Self-service and permission-free: the account being edited is the account
 * that is signed in. Administrators change *other* people's name and picture
 * from Settings → Users, which is a different screen with a different gate.
 *
 * The email address is shown but not editable. It is what this account signs
 * in with, so moving it is an administrator's act — a session someone else got
 * hold of must not be enough to point the account at an inbox they control.
 * The server refuses an `email` in this payload rather than ignoring it.
 *
 * ── Why the store is written by hand after each call ──────────────────────
 *
 * The avatar is painted from `authStore.user.avatarFileName` in the sidebar
 * footer and from the React Query directory everywhere else. Re-hydrating the
 * whole session with `/auth/me` would repaint both, but it also re-resolves
 * permissions and models for a change that touched one string. Writing the new
 * file name into the store and invalidating the directory query is the narrow
 * version of the same repaint, and it is instant.
 */
import { USER_NAME_MAX_LENGTH } from "@taskin-code/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { AuthClient } from "../../auth/AuthClient";
import { useAuthStore } from "../../auth/authStore";
import { readFileAsDataUrl } from "../../lib/readFileAsDataUrl";
import { userDirectoryQueryKey } from "../../lib/userDirectoryReactQuery";
import { UserAvatar } from "../UserAvatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PlaneCredentialCard } from "./PlaneCredentialCard";
import { AVATAR_FILE_INPUT_ACCEPT, validateAvatarFile } from "./ProfileSection.logic";

export function ProfileSection() {
  const user = useAuthStore((state) => state.user);
  const signedIn = useAuthStore((state) => state.status === "authenticated");
  const applyUser = useAuthStore((state) => state.applyUser);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState(user?.name ?? "");
  const [nameSaved, setNameSaved] = useState(false);

  // Follows the account, not the keystrokes: an admin renaming this user (or a
  // second tab doing it) repaints the field, while a draft in progress is left
  // alone because the effect only reruns when the stored name actually changes.
  const storedName = user?.name ?? "";
  useEffect(() => {
    setNameDraft(storedName);
  }, [storedName]);

  const heading = (
    <div className="min-w-0">
      <h3 className="text-base font-semibold">Profile</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Your name and photo, as your teammates see them in the sidebar, in conversations, and
        wherever someone picks a person to share a thread with.
      </p>
    </div>
  );

  if (!signedIn || user === null) {
    return (
      <div className="flex flex-col gap-4">
        {heading}
        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
          This server is running without authentication, so there is no account to edit.
        </div>
      </div>
    );
  }

  /**
   * Applies the new file name locally and drops the cached directory.
   *
   * The directory is cached for a minute, so without the invalidation the
   * sidebar would show the new photo while the assignee picker and the message
   * timeline kept the old one for up to that long.
   */
  const settle = (avatarFileName: string | null) => {
    applyUser({ ...user, avatarFileName });
    void queryClient.invalidateQueries({ queryKey: userDirectoryQueryKey });
  };

  /** Same repaint as `settle`, for the other field the directory carries. */
  const settleName = (name: string) => {
    applyUser({ ...user, name });
    void queryClient.invalidateQueries({ queryKey: userDirectoryQueryKey });
  };

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared immediately so picking the same file twice still fires a change
    // event — otherwise a failed upload could not be retried with the same
    // photo without choosing a different one first.
    event.target.value = "";
    if (!file) return;

    const verdict = validateAvatarFile(file);
    if (!verdict.ok) {
      setError(verdict.message);
      return;
    }

    void run(async () => {
      const dataUrl = await readFileAsDataUrl(file);
      settle(await AuthClient.setAvatar(dataUrl));
    });
  };

  const remove = () =>
    void run(async () => {
      await AuthClient.removeAvatar();
      settle(null);
    });

  const trimmedName = nameDraft.trim();
  const nameChanged = trimmedName.length > 0 && trimmedName !== user.name;

  const saveName = (event: React.FormEvent) => {
    event.preventDefault();
    if (!nameChanged) return;
    void run(async () => {
      // The server's version is what lands in the store: it trimmed the input,
      // and echoing it back is what keeps the field and the sidebar identical.
      const saved = await AuthClient.updateProfileName(trimmedName);
      settleName(saved);
      setNameDraft(saved);
      setNameSaved(true);
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {heading}

      <div className="flex flex-col gap-4 rounded-md border border-border/60 bg-card/40 p-6">
        <div className="flex items-center gap-4">
          <UserAvatar
            userId={user.id}
            displayName={user.name}
            avatarFileName={user.avatarFileName}
            size="lg"
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={AVATAR_FILE_INPUT_ACCEPT}
            className="hidden"
            onChange={onFilePicked}
          />
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            {busy ? "Working…" : user.avatarFileName ? "Change photo" : "Upload photo"}
          </Button>
          {user.avatarFileName ? (
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove}>
              Remove photo
            </Button>
          ) : null}
          <span className="text-xs text-muted-foreground">PNG, JPEG, GIF or WebP, up to 5 MB.</span>
        </div>

        <form className="flex flex-col gap-3 border-t border-border/50 pt-4" onSubmit={saveName}>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs" htmlFor="profile-name">
              Display name
            </Label>
            <Input
              id="profile-name"
              value={nameDraft}
              maxLength={USER_NAME_MAX_LENGTH}
              disabled={busy}
              onChange={(event) => {
                setNameDraft(event.target.value);
                setNameSaved(false);
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs" htmlFor="profile-email">
              Email
            </Label>
            <Input id="profile-email" value={user.email} readOnly disabled />
            <p className="text-xs text-muted-foreground">
              This is what you sign in with. Only an administrator can change it.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" size="sm" disabled={busy || !nameChanged}>
              {busy ? "Saving…" : "Save name"}
            </Button>
            {nameSaved && !nameChanged ? (
              <span className="text-xs text-muted-foreground">Saved.</span>
            ) : null}
          </div>
        </form>

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}
      </div>

      {/*
        Your Plane account belongs here and not in Settings → Plane: that screen
        configures the installation and is an admin's, while this credential is
        the one thing about Plane that only its owner may type.
      */}
      <PlaneCredentialCard />
    </div>
  );
}
